/**
 * Regras da folha de etiquetas adesivas (A4 Pimaco 65): geometria em mm,
 * montagem das folhas a partir da lista de produtos e validações.
 */

/** Folha A4 com 65 etiquetas de 38,1 × 21,2 mm (5 colunas × 13 linhas) */
export const SHEET = {
  name: "Folha A4 Pimaco 65",
  pageWidth: 210,
  pageHeight: 297,
  columns: 5,
  rows: 13,
  labelWidth: 38.1,
  labelHeight: 21.2,
  marginTop: 10.7,
  marginLeft: 4.7,
  gapX: 2.5,
  gapY: 0,
};

export const LABELS_PER_SHEET = SHEET.columns * SHEET.rows;

// Até 10 folhas por produto (evita PDF gigante por erro de digitação)
export const MAX_QUANTITY = LABELS_PER_SHEET * 10;
export const MAX_NAME_LENGTH = 60;
export const MAX_NOTE_LENGTH = 60;
export const MAX_OFFSET_MM = 5;

export const DEFAULT_LAYOUT = { offsetX: 0, offsetY: 0, showBorders: false };

const pad = (n) => String(n).padStart(2, "0");

/** Hoje no formato do input date (yyyy-mm-dd), no fuso local */
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * "2026-10-21" → "21/10/2026". Não usa `new Date(iso)`: a string seria lida
 * em UTC e, no Brasil, imprimiria o dia anterior.
 */
export const formatIsoDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
};

/** Número (botões −/+) ou texto digitado ("12a" → 12), limitado a 0..MAX_QUANTITY */
export const clampQuantity = (value) => {
  const raw = typeof value === "number" ? value : String(value).replace(/\D/g, "");
  const n = Math.floor(Number(raw) || 0);
  return Math.min(MAX_QUANTITY, Math.max(0, n));
};

let nextEntryId = 0;

export const createEntry = () => ({
  id: ++nextEntryId,
  name: "",
  quantity: 1,
  useFabricationDate: true,
  fabrication: todayIso(),
  fabricationText: "",
  useValidityDate: true,
  validity: "",
  validityText: "",
  hasQrCode: false,
  qrCodeText: "",
  note: "",
});

export const isPrintable = (entry) =>
  Boolean(entry.name.trim()) && entry.quantity > 0;

/** Validade anterior à fabricação (só quando as duas são datas) */
export const hasDateConflict = (entry) =>
  entry.useFabricationDate &&
  entry.useValidityDate &&
  Boolean(entry.fabrication) &&
  Boolean(entry.validity) &&
  entry.validity < entry.fabrication;

/** Texto impresso na linha de fabricação/validade ("" = linha em branco) */
const dateLine = (useDate, iso, text) =>
  useDate ? formatIsoDate(iso) : text.trim();

/** Conteúdo da etiqueta de um item da lista */
export const toLabel = (entry, itemIndex) => {
  const name = entry.name.trim();
  const fabrication = dateLine(entry.useFabricationDate, entry.fabrication, entry.fabricationText);
  const validity = dateLine(entry.useValidityDate, entry.validity, entry.validityText);
  const note = entry.note.trim();
  const fabLine = fabrication && (entry.useFabricationDate ? `Fab: ${fabrication}` : fabrication);
  const valLine = validity && (entry.useValidityDate ? `Val: ${validity}` : validity);

  // QR sem conteúdo informado leva os próprios dados da etiqueta (lote/rastreio)
  const qr = entry.hasQrCode
    ? entry.qrCodeText.trim() || [name, fabLine, valLine, note].filter(Boolean).join("\n")
    : null;

  return { itemIndex, name, fabLine, valLine, note, qr };
};

/**
 * Distribui as etiquetas em folhas de 65. As `startAt` primeiras posições da
 * 1ª folha ficam como "skipped" (folha já usada em parte).
 * Cada posição: { label } | "skipped" | null (em branco).
 */
export const buildSheets = (entries, startAt = 0) => {
  const slots = Array(startAt).fill("skipped");
  entries.forEach((entry, index) => {
    if (!isPrintable(entry)) return;
    const label = toLabel(entry, index);
    for (let i = 0; i < entry.quantity; i += 1) slots.push({ label });
  });

  const used = slots.length;
  const pageCount = Math.max(1, Math.ceil(used / LABELS_PER_SHEET));
  while (slots.length < pageCount * LABELS_PER_SHEET) slots.push(null);

  const pages = [];
  for (let p = 0; p < pageCount; p += 1) {
    pages.push(slots.slice(p * LABELS_PER_SHEET, (p + 1) * LABELS_PER_SHEET));
  }

  return {
    pages,
    printed: used - startAt,
    pageCount,
    // Posições ocupadas (impressas + puladas) na última folha
    lastPageUsed: used - (pageCount - 1) * LABELS_PER_SHEET,
  };
};

/** Posição (mm) do canto superior esquerdo da etiqueta `index` na folha */
export const slotPosition = (index, layout = DEFAULT_LAYOUT) => {
  const col = index % SHEET.columns;
  const row = Math.floor(index / SHEET.columns);
  return {
    left: SHEET.marginLeft + layout.offsetX + col * (SHEET.labelWidth + SHEET.gapX),
    top: SHEET.marginTop + layout.offsetY + row * (SHEET.labelHeight + SHEET.gapY),
  };
};
